import { storage } from "@/firebase/storage";
import {
    ref,
    uploadBytes,
    uploadBytesResumable,
    deleteObject,
    getDownloadURL
} from "firebase/storage";

export interface FirebaseUploadResult {
    filename: string;
    downloadUrl: string;
}

export type UploadProgressCallback = (progress: number) => void;

export class FirebaseStorageAdapter {
    /**
     * Upload a file directly to Firebase Storage with optional progress tracking
     * Uses the same path structure as your current signed URL approach
     */
    async uploadFile(
        file: File,
        uuid: string,
        storageUploadDir: string = "anonymous",
        onProgress?: UploadProgressCallback
    ): Promise<FirebaseUploadResult> {
        try {
            const filename = `${storageUploadDir}/${uuid}`;
            const storageRef = ref(storage, filename);

            // Use resumable upload if progress callback is provided
            if (onProgress) {
                return await new Promise<FirebaseUploadResult>((resolve, reject) => {
                    const uploadTask = uploadBytesResumable(storageRef, file, {
                        contentType: file.type
                    });

                    uploadTask.on(
                        "state_changed",
                        (snapshot) => {
                            const progress =
                                (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                            onProgress(Math.round(progress));
                        },
                        (error) => {
                            console.error("🔥 [FIREBASE STORAGE] ❌ Upload failed:", error);
                            reject(new Error(`Firebase Storage upload failed: ${error}`));
                        },
                        async () => {
                            const downloadUrl = await getDownloadURL(uploadTask.snapshot.ref);
                            resolve({ filename: uploadTask.snapshot.ref.fullPath, downloadUrl });
                        }
                    );
                });
            }

            // Fallback to simple upload without progress
            const snapshot = await uploadBytes(storageRef, file, { contentType: file.type });
            const downloadUrl = await getDownloadURL(snapshot.ref);

            return { filename: snapshot.ref.fullPath, downloadUrl };
        } catch (error: any) {
            console.error("🔥 [FIREBASE STORAGE] ❌ Upload failed:", error);
            console.error("🔥 [FIREBASE STORAGE] Error details:", {
                code: error.code,
                message: error.message,
                serverResponse: error.serverResponse,
                customData: error.customData
            });

            // Log the full error object to see all available details
            console.error("🔥 [FIREBASE STORAGE] Full error object:", error);
            throw new Error(`Firebase Storage upload failed: ${error}`);
        }
    }

    /**
     * Delete a file from Firebase Storage
     */
    async deleteFile(filename: string): Promise<void> {
        try {
            const storageRef = ref(storage, filename);
            await deleteObject(storageRef);
        } catch (error) {
            console.error("🔥 [FIREBASE STORAGE] ❌ Delete failed:", error);
            throw new Error(`Firebase Storage delete failed: ${error}`);
        }
    }

    /**
     * Get download URL for a file
     */
    async getDownloadUrl(filename: string): Promise<string> {
        try {
            const storageRef = ref(storage, filename);
            return await getDownloadURL(storageRef);
        } catch (error) {
            console.error("🔥 [FIREBASE STORAGE] ❌ Get URL failed:", error);
            throw new Error(`Firebase Storage get URL failed: ${error}`);
        }
    }
}

export const firebaseStorage = new FirebaseStorageAdapter();
